import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Car, MapPin, Clock, FileText, Lock, ParkingSquare, Bell, Radio, Navigation, Clipboard } from 'lucide-react';

const patrol = "https://images.unsplash.com/photo-1764565690092-04131ab352c3?w=800";

const patrolServices = [
  {
    icon: <Car className="h-6 w-6" />,
    title: 'Scheduled Mobile Patrols',
    description: 'Marked patrol vehicles conduct timed property checks at intervals you define — hourly, bi-hourly, or custom schedules. Each visit includes a full perimeter check, access point verification, and detailed reporting. Consistent visible presence deters criminal activity and gives tenants and stakeholders peace of mind.',
    ideal: 'Ideal for commercial properties, office parks, and residential communities.'
  },
  {
    icon: <Navigation className="h-6 w-6" />,
    title: 'Randomized Deterrent Patrols',
    description: 'Criminals study predictable security patterns. Our randomized patrol methodology varies routes, timing, and check sequences to eliminate predictability. GPS tracking verifies every patrol while our dispatch center monitors officer location and status in real time.',
    ideal: 'Recommended for high-crime areas and properties with recurring issues.'
  },
  {
    icon: <MapPin className="h-6 w-6" />,
    title: 'GPS-Tracked Mobile Units',
    description: 'Every patrol vehicle is equipped with real-time GPS tracking. Our dispatch center monitors all units to ensure accountability, optimize response times, and provide clients with verifiable proof of patrol coverage. GPS data is archived and available for client review upon request.',
    ideal: 'Standard across all patrol operations for full transparency.'
  },
  {
    icon: <Bell className="h-6 w-6" />,
    title: 'Alarm Response Services',
    description: 'When your alarm system activates, our patrol officers respond rapidly to assess the situation, secure the premises, and coordinate with law enforcement if needed. Faster than relying on police response alone — our officers are already in your area conducting patrols and can arrive within minutes.',
    ideal: 'Essential for properties with monitored alarm systems.'
  },
  {
    icon: <Lock className="h-6 w-6" />,
    title: 'Lock & Unlock Services',
    description: 'Our patrol officers can open and secure your property on a set schedule — unlocking doors for morning staff and locking up after business hours. Each lock/unlock is documented with timestamps and officer verification, eliminating the need to distribute keys to multiple employees.',
    ideal: 'Perfect for businesses, property managers, and multi-tenant buildings.'
  },
  {
    icon: <ParkingSquare className="h-6 w-6" />,
    title: 'Parking Enforcement',
    description: 'Patrol officers enforce parking regulations in your lot or structure — issuing warnings, documenting violations, coordinating towing of unauthorized vehicles, and maintaining traffic flow. We work with property management to enforce specific rules for reserved spaces, fire lanes, and handicap zones.',
    ideal: 'Ideal for shopping centers, apartment complexes, and commercial lots.'
  },
  {
    icon: <FileText className="h-6 w-6" />,
    title: 'Detailed Patrol Reports',
    description: 'Every patrol generates a comprehensive report including timestamps, photos, officer observations, and any incidents or anomalies discovered. Reports are delivered digitally and archived for your records. Our reporting system provides a clear audit trail of security coverage for insurance and compliance purposes.',
    ideal: 'Included with every patrol service at no additional cost.'
  },
  {
    icon: <Radio className="h-6 w-6" />,
    title: 'Incident Response & Coordination',
    description: 'When our patrol officers encounter an incident — trespassing, vandalism, break-in attempt, or suspicious activity — they follow established response protocols: assess, contain, document, and coordinate with law enforcement. Our dispatch center manages communication and ensures proper follow-through on every event.',
    ideal: 'Available 24/7 with direct dispatch center communication.'
  }
];

const patrolFeatures = [
  { icon: <Shield className="h-5 w-5" />, text: 'Marked Patrol Vehicles for Maximum Visibility' },
  { icon: <Navigation className="h-5 w-5" />, text: 'Real-Time GPS Tracking & Verification' },
  { icon: <Clipboard className="h-5 w-5" />, text: 'Digital Reports with Photo Documentation' },
  { icon: <Clock className="h-5 w-5" />, text: '24/7 Dispatch Center Coordination' },
  { icon: <CheckCircle className="h-5 w-5" />, text: 'Licensed & Insured Officers' },
  { icon: <Radio className="h-5 w-5" />, text: 'Direct Radio Communication' }
];

export const PatrolSection = () => {
  return (
    <div id="patrol-services" className="py-20 bg-[#0A0A0A]" data-testid="service-section-patrol-services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-2">Mobile Patrol Operations</p>
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
              Security Patrols.<br /><span className="text-[#F5C518]">Visible. Verified. Reliable.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              Our mobile patrol division provides consistent, documented security coverage across Southern California. GPS-tracked patrol vehicles, randomized routes, and comprehensive reporting ensure your property receives the protection it deserves — with full accountability and transparency at every step.
            </p>
            <div className="bg-[#121212] border-l-4 border-[#F5C518] p-4 mb-6">
              <p className="text-white font-semibold">Every patrol is tracked.</p>
              <p className="text-[#F5C518]">Every visit is documented. Every report is delivered.</p>
            </div>
            <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-[#E5B508] transition-all duration-300" data-testid="service-cta-patrol-services">
              <span>Request Patrol Services</span>
            </Link>
          </div>
          <div className="relative">
            <img src={patrol} alt="Security Patrol Vehicle" className="w-full aspect-[4/3] object-cover border border-zinc-800" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/50 to-transparent" />
          </div>
        </div>

        {/* Service Capabilities */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-8 font-['Barlow_Condensed'] text-center">
            Patrol <span className="text-[#F5C518]">Services</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {patrolServices.map((service) => (
              <div key={service.title} className="bg-[#121212] border border-zinc-800 p-6 hover:border-[#F5C518]/50 transition-colors">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#F5C518]/10 text-[#F5C518] mb-4">
                  {service.icon}
                </div>
                <h4 className="text-lg font-bold text-white mb-3 font-['Barlow_Condensed'] uppercase">{service.title}</h4>
                <p className="text-zinc-400 text-sm mb-3">{service.description}</p>
                <p className="text-[#F5C518] text-xs font-semibold">{service.ideal}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Features & Coverage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div className="bg-[#121212] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Patrol <span className="text-[#F5C518]">Features</span>
            </h3>
            <div className="space-y-4">
              {patrolFeatures.map((item) => (
                <div key={item.text} className="flex items-center text-zinc-400">
                  <span className="text-[#F5C518] mr-3">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>
            <p className="text-zinc-500 text-sm mt-6 pt-6 border-t border-zinc-800">
              Full accountability on every patrol. No exceptions.
            </p>
          </div>

          <div className="bg-[#121212] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Coverage <span className="text-[#F5C518]">Options</span>
            </h3>
            <p className="text-zinc-400 mb-4">Patrol packages designed for every need and budget:</p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Hourly patrol checks (1x, 2x, or 4x per hour)
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Overnight-only coverage (6PM - 6AM)
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                24/7 continuous patrol operations
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Weekend and holiday supplemental patrols
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Multi-property route packages
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Combined patrol + alarm response contracts
              </li>
            </ul>
            <p className="text-[#F5C518] text-sm font-semibold">
              Service Areas: Orange County, Los Angeles, Inland Empire, and surrounding regions.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#121212] border border-[#F5C518]/30 p-8 text-center">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
            Secure Your Property with <span className="text-[#F5C518]">Mobile Patrols</span>
          </h3>
          <p className="text-zinc-400 mb-6 max-w-2xl mx-auto">
            Get a customized patrol plan with documented coverage and real-time tracking. Contact us for a free property assessment.
          </p>
          <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300">
            <span>Get Patrol Quote</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
